import { sentryInterceptor } from '@/clients/sentryInterceptor.ts';
import { environment } from '@/environment.ts';
import { useAuthTokenContext } from '@/shared/context/AuthTokenContext.tsx';
import { MarketCode } from '@schuettflix/utils';
import type { DocumentManagementBridge } from '@schuettflix/openapi-specs';
import axios, { AxiosInstance } from 'axios';
import createFetchClient, { Client } from 'openapi-fetch';
import createQueryClient, { OpenapiQueryClient } from 'openapi-react-query';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { createApiClient as createLocationClient } from '@/generated/location-client/api-client';
import { useTranslation } from 'react-i18next';

export type HttpClientContextType = {
    constructionProjectClient: AxiosInstance;
    customRequestClient: AxiosInstance;
    fulfillmentClient: AxiosInstance;
    manualFulfillmentClient: AxiosInstance;
    monolithClient: AxiosInstance;
    orderClient: AxiosInstance;
    orderClientV2: AxiosInstance;
    positionClient: AxiosInstance;
    positionClientV2: AxiosInstance;
    salesforceDealImporterClient: AxiosInstance;
    getMarketScopedOrderClient: (marketCode: MarketCode) => AxiosInstance;
    readonly documentManagementBridgeClient: {
        readonly fetch: Client<DocumentManagementBridge.paths>;
        readonly query: OpenapiQueryClient<DocumentManagementBridge.paths>;
    };
    locationClient: ReturnType<typeof createLocationClient>;
};

type HttpClientContextProviderProps = PropsWithChildren & {
    marketCode: MarketCode;
};

type Headers = {
    'Accept-Language': string;
    Authorization: string;
    'X-Client-Version': string;
    'X-Operating-Market': MarketCode;
};

const HttpClientContext = createContext<HttpClientContextType | undefined>(undefined);

function createClient(baseUrl: string, headers: Headers) {
    const axiosClient = axios.create({
        baseURL: baseUrl,
        headers: { ...headers },
    });
    axiosClient.interceptors.request.use(sentryInterceptor);
    return axiosClient;
}

export function HttpClientContextProvider({ children, marketCode }: HttpClientContextProviderProps) {
    const { getToken } = useAuthTokenContext();
    const { i18n } = useTranslation();

    let tokenValue: string = '';
    try {
        tokenValue = getToken();
    } catch (e) {
        throw new Error('Unable to render HttpClientContextProvider', { cause: e });
    }

    const value = useMemo(() => {
        const headers = {
            'Accept-Language': i18n.language,
            Authorization: `Bearer ${tokenValue}`,
            'X-Client-Version': environment.FRONTEND_MONOLITH_APP_VERSION,
            'X-Operating-Market': marketCode,
        };

        const constructionProjectClient = createClient(environment.CONSTRUCTION_PROJECT_BASE_URL, headers);
        const customRequestClient = createClient(environment.CUSTOM_REQUEST_SERVICE_BASE_URL, headers);
        const fulfillmentClient = createClient(environment.FULFILLMENT_SERVICE_BASE_URL, headers);
        const manualFulfillmentClient = createClient(environment.MANUAL_FULFILLMENT_SERVICE_BASE_URL, headers);
        const monolithClient = createClient(environment.MONOLITH_BASE_URL, headers);
        const orderClient = createClient(environment.ORDER_SERVICE_BASE_URL, headers);
        const orderClientV2 = createClient(environment.ORDER_SERVICE_BASE_URL_V2, headers);
        const positionClient = createClient(environment.POSITION_SERVICE_BASE_URL, headers);
        const positionClientV2 = createClient(environment.POSITION_SERVICE_BASE_URL_V2, headers);
        const salesforceDealImporterClient = createClient(environment.SALESFORCE_DEAL_IMPORTER_SERVICE_URL, headers);

        const documentManagementBridgeFetch = createFetchClient<DocumentManagementBridge.paths>({
            baseUrl: environment.DOCUMENT_MANAGEMENT_BRIDGE_BASE_URL,
            headers,
        });
        const documentManagementBridgeQuery = createQueryClient(documentManagementBridgeFetch);
        const documentManagementBridgeClient = {
            fetch: documentManagementBridgeFetch,
            query: documentManagementBridgeQuery,
        } as const;

        const locationClient = createLocationClient(environment.LOCATION_SERVICE_URL, {
            axiosInstance: createClient(environment.LOCATION_SERVICE_URL, headers),
        });

        function getMarketScopedOrderClient(marketCode: MarketCode): AxiosInstance {
            return createClient(environment.ORDER_SERVICE_BASE_URL, {
                ...headers,
                'X-Operating-Market': marketCode,
            });
        }

        return {
            constructionProjectClient,
            customRequestClient,
            fulfillmentClient,
            manualFulfillmentClient,
            monolithClient,
            orderClient,
            orderClientV2,
            positionClient,
            positionClientV2,
            salesforceDealImporterClient,
            getMarketScopedOrderClient,
            documentManagementBridgeClient,
            locationClient,
        };
    }, [tokenValue, marketCode, i18n.language]);

    return <HttpClientContext.Provider value={value}>{children}</HttpClientContext.Provider>;
}

export function useHttpClientContext() {
    const context = useContext(HttpClientContext);
    if (!context) {
        throw new Error('HttpClientContext must be provided!');
    }
    return context;
}
